import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/api/client";

/* GET /api/me/notifications */
export interface NotificationItem {
  id: string;
  event_type: string;
  title: string;
  body: string | null;
  /** In-app path to open, e.g. /records/holiday_request/<id>. */
  link: string | null;
  created_at: string;
  read_at: string | null;
}

/** Something the person must act on; it stays until done (today: approvals waiting for their decision). */
export interface TodoItem {
  /** Approval request id. */
  id: string;
  kind: "approval";
  /** "Holiday request" */
  label: string;
  title: string;
  requested_by: string | null;
  created_at: string;
  link: string | null;
  entity: string;
  entity_id: string;
}

export interface NotificationList {
  items: NotificationItem[];
  unread: number;
  todos: TodoItem[];
}

export const notificationsKey = ["me", "notifications"] as const;

/** Polls every minute (and on focus) so the badge stays current without a socket. */
export function useNotifications() {
  return useQuery({
    queryKey: notificationsKey,
    queryFn: () => apiFetch<NotificationList>("/me/notifications", { query: { limit: 30 } }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/me/notifications/${encodeURIComponent(id)}/read`, { method: "POST" }),
    onMutate: (id) => {
      qc.setQueryData<NotificationList>(notificationsKey, (old) =>
        old
          ? {
              ...old,
              unread: Math.max(0, old.unread - (old.items.some((n) => n.id === id && !n.read_at) ? 1 : 0)),
              items: old.items.map((n) => (n.id === id && !n.read_at ? { ...n, read_at: new Date().toISOString() } : n)),
            }
          : old,
      );
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: notificationsKey }),
  });
}

/** Marks the given notifications read (the pane calls this for what it has shown). */
export function useMarkSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => apiFetch<void>("/me/notifications/read", { method: "POST", body: { ids } }),
    onSettled: () => void qc.invalidateQueries({ queryKey: notificationsKey }),
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<void>("/me/notifications/read-all", { method: "POST" }),
    onMutate: () => {
      const now = new Date().toISOString();
      qc.setQueryData<NotificationList>(notificationsKey, (old) =>
        old ? { ...old, unread: 0, items: old.items.map((n) => (n.read_at ? n : { ...n, read_at: now })) } : old,
      );
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: notificationsKey }),
  });
}

/** The API sends naive UTC timestamps; read them as UTC. */
export function utc(value: string): Date {
  return new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`);
}

const rtf = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });
const dayFmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" });

/** "just now", "5 minutes ago", "yesterday", "3 Oct". */
export function ago(value: string, now: Date = new Date()): string {
  const secs = Math.round((utc(value).getTime() - now.getTime()) / 1000);
  const abs = Math.abs(secs);
  if (abs < 45) return "just now";
  if (abs < 3600) return rtf.format(Math.round(secs / 60), "minute");
  if (abs < 86_400) return rtf.format(Math.round(secs / 3600), "hour");
  if (abs < 7 * 86_400) return rtf.format(Math.round(secs / 86_400), "day");
  return dayFmt.format(utc(value));
}
